import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { GridFilterModel, GridSortItem, GridSortModel } from '@mui/x-data-grid';
import { StoredUser } from 'store/features/authentication';
import { Device, NapiResultType } from 'types';
import DeviceInformationMainCard from 'views/DeviceDetails/views/DeviceInformation/cards/DeviceInformationMainCard/DeviceInformationMainCard';
import styles from './TanksPanelView.module.scss';
import {
    DoublePanelResizeEvent,
    ResizableDoublePanel,
} from '../ResizableDoublePanel/ResizableDoublePanel';
import { TransformingTanksList } from '../TransformingTanksList/TransformingTanksList';
import { useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export interface TanksPanelViewProps {
    data: NapiResultType<Device[]>;
    loading: boolean;
    fetching: boolean;
    user: StoredUser | null;
    onFilterModelChange: Dispatch<SetStateAction<GridFilterModel | undefined>>;
    onPageChange: Dispatch<SetStateAction<number>>;
    onPageSizeChange: Dispatch<SetStateAction<number>>;
    onSortModelChange: Dispatch<SetStateAction<GridSortItem[]>>;
    page: number;
    sortModel?: GridSortModel;
}

export function TanksPanelView({
    data,
    loading,
    fetching,
    user,
    onFilterModelChange,
    onPageChange,
    onPageSizeChange,
    onSortModelChange,
    page,
    sortModel,
}: TanksPanelViewProps) {
    const navigate = useNavigate();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [columnsEnabled, setColumnsEnabled] = useState<boolean>(false);
    const [leftWidth, setLeftWidth] = useState<number>(0);

    const handleListClick = (deviceId: string, device: Device) => {
        if (!isTablet) setSelectedDevice(device);
        else navigate(`/device-details/${deviceId}`);
    };
    const onResize = (event: DoublePanelResizeEvent) => {
        setLeftWidth(event.leftWidth);
    };
    const onDetailsClick = (selectedDevice: Device) => {
        navigate(`/device-details/${selectedDevice.deviceId}`);
    };

    useEffect(() => {
        if (leftWidth >= 755) setColumnsEnabled(true);
        else setColumnsEnabled(false);
    }, [leftWidth]);
    useEffect(() => {
        setSelectedDevice(data.results[0]);
    }, [data]);

    return (
        <div data-testid="TanksPanelView" className={styles.container}>
            {!isTablet ? (
                <ResizableDoublePanel
                    leftPanel={
                        <div className={styles.leftPane}>
                            <TransformingTanksList
                                data={data}
                                loading={loading}
                                fetching={fetching}
                                columnsEnabled={columnsEnabled}
                                onDeviceSelected={handleListClick}
                                onFilterModelChange={onFilterModelChange}
                                onPageChange={onPageChange}
                                onPageSizeChange={onPageSizeChange}
                                onSortModelChange={onSortModelChange}
                                sortModel={sortModel}
                                page={page}
                                selectedDeviceId={
                                    selectedDevice?.deviceId || ''
                                }
                            />
                        </div>
                    }
                    rightPanel={
                        <div className={styles.rightPane}>
                            {!!selectedDevice && (
                                <DeviceInformationMainCard
                                    device={selectedDevice}
                                    user={user}
                                    onClickViewDetails={() =>
                                        onDetailsClick(selectedDevice)
                                    }
                                />
                            )}
                            {!selectedDevice && (
                                <div className={styles.detailsPlaceholder}>
                                    Select a tank to see details
                                </div>
                            )}
                        </div>
                    }
                    onResize={onResize}
                />
            ) : (
                <TransformingTanksList
                    data={data}
                    loading={loading}
                    fetching={fetching}
                    columnsEnabled={columnsEnabled}
                    onDeviceSelected={handleListClick}
                    onFilterModelChange={onFilterModelChange}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                    onSortModelChange={onSortModelChange}
                    sortModel={sortModel}
                    page={page}
                    selectedDeviceId={selectedDevice?.deviceId || ''}
                />
            )}
        </div>
    );
}
